import { type Observable as ObservableType, Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/of';
import 'rxjs/add/operator/map';
import { fromPromise } from 'rxjs/observable/fromPromise';
import type { aggBacklogDataQuery$data } from '@atlassian/jira-relay/src/__generated__/aggBacklogDataQuery.graphql';
import fetchAgilityGqlData from '@atlassian/jira-software-backlog-load-agility-graphql-data';
import type { PrefetchedDataGql } from '@atlassian/jira-software-backlog-load-agility-graphql-data/src/types';
import type { FieldConfigs, StoreFieldsActions } from '../../common/hooks/use-store-fields';
import { isBacklogWithRelayEnabled } from '../../feature-flags';
import type { SetAppDataPayload } from '../../state/actions/app';
import { transformAggData } from './agg-transformer';
import transformer from './transformer';

function updateStoreFields<T>(
	data: T,
	fieldConfigs?: FieldConfigs,
	updateFieldsAction?: (data: T, fieldConfigs: FieldConfigs) => void,
): void {
	if (updateFieldsAction && fieldConfigs) {
		updateFieldsAction(data, fieldConfigs);
	}
}

// eslint-disable-next-line jira/import/no-anonymous-default-export
export default ({
	baseUrl,
	boardId,
	isCompanyManaged,
	fieldConfigs,
	prefetchedData,
	prefetchedAggData,
	storeFieldsActions,
	fetchDataFromAGG,
}: {
	baseUrl: string;
	boardId: string;
	isCompanyManaged: boolean;
	fieldConfigs?: FieldConfigs;
	prefetchedData?: PrefetchedDataGql | null;
	prefetchedAggData?: aggBacklogDataQuery$data | null;
	storeFieldsActions?: StoreFieldsActions | null;
	fetchDataFromAGG?: () => Promise<aggBacklogDataQuery$data>;
}): ObservableType<SetAppDataPayload> => {
	if (isBacklogWithRelayEnabled()) {
		if (prefetchedAggData) {
			updateStoreFields(
				prefetchedAggData,
				fieldConfigs,
				storeFieldsActions?.storeFieldsFromPrefetchedAggData,
			);
			return Observable.of(transformAggData(prefetchedAggData));
		}
		if (fetchDataFromAGG) {
			return fromPromise(fetchDataFromAGG()).map((data) => {
				updateStoreFields(data, fieldConfigs, storeFieldsActions?.storeFieldsFromPrefetchedAggData);
				return transformAggData(data);
			});
		}
		return fetchAgilityGqlData(baseUrl, boardId).map((data) => {
			updateStoreFields(data, fieldConfigs, storeFieldsActions?.storeFieldsFromPrefetchedData);
			return transformer(data, isCompanyManaged);
		});
	}

	const getResponse = () => {
		if (prefetchedData) {
			updateStoreFields(
				prefetchedData,
				fieldConfigs,
				storeFieldsActions?.storeFieldsFromPrefetchedData,
			);
			return Observable.of(prefetchedData);
		}
		return fetchAgilityGqlData(baseUrl, boardId);
	};

	return getResponse().map((data) => {
		updateStoreFields(data, fieldConfigs, storeFieldsActions?.storeFieldsFromPrefetchedData);
		return transformer(data, isCompanyManaged);
	});
};
